import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function DigestReview({ token }) {
  const [state, setState] = useState('loading'); // loading | ok | notfound | expired | error | reviewed
  const [digest, setDigest] = useState(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/digest/${token}`)
      .then(async res => {
        if (res.status === 404) return setState('notfound');
        if (res.status === 410) return setState('expired');
        if (!res.ok) return setState('error');
        const data = await res.json();
        setDigest(data);
        setState(data.status === 'reviewed' ? 'reviewed' : 'ok');
      })
      .catch(() => setState('error'));
  }, [token]);

  async function markReviewed() {
    setMarking(true);
    try {
      const res = await fetch(`${API}/api/digest/${token}/reviewed`, { method: 'PUT' });
      if (res.ok) setState('reviewed');
      else setState('error');
    } catch {
      setState('error');
    } finally {
      setMarking(false);
    }
  }

  const containerStyle = {
    maxWidth: 640,
    margin: '40px auto',
    fontFamily: 'Georgia, serif',
    color: '#1a1a18',
    padding: '0 16px',
  };

  if (state === 'loading') return (
    <div style={containerStyle}>
      <p style={{ color: '#6b6b63', fontFamily: 'sans-serif' }}>Loading…</p>
    </div>
  );

  if (state === 'notfound') return (
    <div style={containerStyle}>
      <h2 style={{ fontSize: 18 }}>Review link not found</h2>
      <p style={{ color: '#6b6b63', fontFamily: 'sans-serif', fontSize: 14 }}>This link is invalid or has already been used.</p>
    </div>
  );

  if (state === 'expired') return (
    <div style={containerStyle}>
      <h2 style={{ fontSize: 18 }}>Link expired</h2>
      <p style={{ color: '#6b6b63', fontFamily: 'sans-serif', fontSize: 14 }}>This review link expired after 7 days. Log in to view your entries.</p>
    </div>
  );

  if (state === 'error') return (
    <div style={containerStyle}>
      <h2 style={{ fontSize: 18 }}>Something went wrong</h2>
      <p style={{ color: '#6b6b63', fontFamily: 'sans-serif', fontSize: 14 }}>Please try again or log in directly.</p>
    </div>
  );

  const dateStr = String(digest.digest_date).slice(0, 10);
  const dateLabel = new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  const totalHours = (digest.entries || []).reduce((s, e) => s + parseFloat(e.hours || 0), 0);

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ background: '#1a1a18', padding: '18px 24px', borderRadius: '8px 8px 0 0' }}>
        <div style={{ fontSize: 20, color: '#faf9f6', letterSpacing: '0.02em' }}>
          2nd<span style={{ color: '#4a8c62' }}>Counsel</span>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 3, letterSpacing: '0.06em', fontFamily: 'monospace' }}>
          ATTORNEY INTELLIGENCE PLATFORM
        </div>
      </div>

      {/* Body */}
      <div style={{ background: '#faf9f6', border: '1px solid #ddd9d0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 28 }}>
        <p style={{ margin: '0 0 4px', fontSize: 15 }}>Daily Billing Digest</p>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6b6b63', fontFamily: 'sans-serif' }}>{dateLabel}</p>

        {/* Entries table */}
        <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse', border: '1px solid #ddd9d0', marginBottom: 16 }}>
          <thead>
            <tr style={{ background: '#ede9e0' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b6b63', fontWeight: 500 }}>Matter</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b6b63', fontWeight: 500 }}>Hours</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b6b63', fontWeight: 500 }}>Narrative</th>
            </tr>
          </thead>
          <tbody>
            {digest.entries.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ padding: '14px 12px', fontSize: 13, color: '#999', textAlign: 'center', fontFamily: 'sans-serif' }}>
                  No entries recorded for this day.
                </td>
              </tr>
            ) : digest.entries.map((e, i) => (
              <tr key={i}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #e8e4dc', fontSize: 13, verticalAlign: 'top' }}>{e.matter_name || '—'}</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #e8e4dc', fontSize: 13, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{parseFloat(e.hours || 0).toFixed(1)} hrs</td>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #e8e4dc', fontSize: 13, fontStyle: 'italic', verticalAlign: 'top', color: '#555' }}>{e.narrative_final || e.narrative_generated || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ textAlign: 'right', fontSize: 13, fontFamily: 'sans-serif', marginBottom: 24 }}>
          <strong>Total: {totalHours.toFixed(1)} hrs</strong>
        </div>

        {state === 'reviewed' ? (
          <div style={{ textAlign: 'center', padding: '14px', background: '#f0f7f3', border: '1px solid #b8d9c5', borderRadius: 6, fontFamily: 'sans-serif', fontSize: 14, color: '#2d5a3d' }}>
            Reviewed — entries confirmed.
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={markReviewed}
              disabled={marking}
              style={{ background: '#2d5a3d', color: 'white', border: 'none', padding: '12px 32px', borderRadius: 6, fontFamily: 'sans-serif', fontSize: 14, fontWeight: 600, cursor: marking ? 'default' : 'pointer', opacity: marking ? 0.7 : 1 }}
            >
              {marking ? 'Saving…' : 'Confirm & Mark Reviewed'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
